import { useEffect } from 'react';
import {
  type Source,
  type ActivePicklistData,
  type CommandModeAudioPayload,
  type CommandModeKeyPayload,
  type CrossTabNotification,
  type CrossWindowDataRefetch,
  type DictationMessage,
  type Dre,
  type FocusWindow,
  type InputReportEventData,
  type InsertSectionHeader,
  type RecorderVoiceCommandProps,
  type ReporterShortcut,
  type UpdateSectionHeaders,
  type Payload,
  type CancelReportCountdownPayload,
  type DeepLinkPayloads,
  type ReportFacadeBusPayload,
  type WebHidNotification,
  WorklistItemPatchPayload,
  UserSettingsPatchPayload,
} from '../constants/crossTabEvents';
import type { BroadcastChannel } from 'broadcast-channel';
import { NameSpaces } from '../constants/namespaces';
import { createCrossTabSubscription, getSharedChannel } from '../utils/createCrossTabSubscription';

type CrossTabMessage<R> = {
  namespace: NameSpaces;
  payload: Payload<R>;
};

// @ts-expect-error [EN-7967] - TS2394 - This overload signature is not compatible with its implementation signature.
export function useCrossTabEventsListener<R>(
  subscribedNamespace: NameSpaces.DEEP_LINK,
  onEvent: ({ source, payload }: { source: Source; payload: DeepLinkPayloads<R> }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.DICTATION,
  onEvent: ({ source, payload }: { source: Source; payload: DictationMessage }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.FOCUS_WINDOW,
  onEvent: ({ source, payload }: { source: Source; payload: FocusWindow }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.REPORTER_SHORTCUT,
  onEvent: ({ source, payload }: { source: Source; payload: ReporterShortcut }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.INSERT_SECTION_HEADER,
  onEvent: ({ source, payload }: { source: Source; payload: InsertSectionHeader }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.UPDATE_SECTION_HEADERS,
  onEvent: ({ source, payload }: { source: Source; payload: UpdateSectionHeaders }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.CROSS_WINDOW_DATA_REFETCH,
  onEvent: ({ source, payload }: { source: Source; payload: CrossWindowDataRefetch }) => void
): void;
export function useCrossTabEventsListener<R>(
  subscribedNamespace: NameSpaces.CROSS_TAB_NOTIFICATION,
  onEvent: ({ source, payload }: { source: Source; payload: CrossTabNotification<R> }) => void
): void;
export function useCrossTabEventsListener<R>(
  subscribedNamespace: NameSpaces.DRE,
  onEvent: ({ source, payload }: { source: Source; payload: Dre<R> }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.CROSS_WINDOW_MIRROR_LAYOUT,
  onEvent: ({ source, payload }: { source: Source; payload: boolean }) => void
): void;
export function useCrossTabEventsListener<R>(
  subscribedNamespace: NameSpaces.WEBHID_NOTIFICATION,
  onEvent: ({ source, payload }: { source: Source; payload: WebHidNotification<R> }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.INPUTREPORT_EVENT,
  onEvent: ({ source, payload }: { source: Source; payload: InputReportEventData }) => void
): void;
export function useCrossTabEventsListener<R>(
  subscribedNamespace: NameSpaces.RECORDER_VOICE_COMMAND,
  onEvent: ({ source, payload }: { source: Source; payload: RecorderVoiceCommandProps<R> }) => void
): void;
export function useCrossTabEventsListener<R>(
  subscribedNamespace: NameSpaces.ACTIVE_PICKLIST_DATA,
  onEvent: ({ source, payload }: { source: Source; payload: ActivePicklistData<R> }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.CANCEL_REPORT_COUNTDOWN,
  onEvent: ({ source, payload }: { source: Source; payload: CancelReportCountdownPayload }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.REPORT_FACADE,
  onEvent: ({ source, payload }: { source: Source; payload: ReportFacadeBusPayload }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.WORKLIST_ITEM_PATCH,
  onEvent: ({ source, payload }: { source: Source; payload: WorklistItemPatchPayload }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.USER_SETTINGS_PATCH,
  onEvent: ({ source, payload }: { source: Source; payload: UserSettingsPatchPayload }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.COMMAND_MODE_AUDIO,
  onEvent: ({ source, payload }: { source: Source; payload: CommandModeAudioPayload }) => void
): void;
export function useCrossTabEventsListener(
  subscribedNamespace: NameSpaces.COMMAND_MODE_KEY,
  onEvent: ({ source, payload }: { source: Source; payload: CommandModeKeyPayload }) => void
): void;
export function useCrossTabEventsListener<R>(
  subscribedNamespace: NameSpaces,
  onEvent: ({ source, payload }: { source: Source; payload: Payload<R> }) => void
) {
  useEffect(() => {
    const bc = getSharedChannel() as BroadcastChannel<CrossTabMessage<R>>;
    return createCrossTabSubscription<R>(subscribedNamespace, onEvent, bc);
  }, [subscribedNamespace, onEvent]);
}
